
import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Moon, 
  Sun, 
  User, 
  Settings, 
  LogOut, 
  MessageSquare, 
  BarChart3 
} from "lucide-react";
import { useTheme } from "next-themes";
import { supabase } from "@/integrations/supabase/client";
import SignupButton from "./SignupButton";
import UserProfileMenu from "./navigation/UserProfileMenu";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";

export function NavigationBar() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [session, setSession] = React.useState(null);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const { theme, setTheme } = useTheme();
  const navRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        checkAdminStatus(session.user.id);
      } else {
        setIsAdmin(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user) {
        checkAdminStatus(session.user.id);
      } else {
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async (userId: string) => {
    try {
      const { data: isAdminResult, error } = await supabase.rpc('is_admin');
      
      if (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        return;
      }
      
      setIsAdmin(isAdminResult || false);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isMenuOpen &&
        navRef.current &&
        !navRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    }

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsMenuOpen(false);
  };

  return (
    <nav
      ref={navRef}
      className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 w-full border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex items-center space-x-1">
              <img src="/orb1.gif" alt="Logo" className="h-12 w-12" />
              <div className="text-xl font-bold text-primary hidden sm:block">
                Generative Toolbox
              </div>
            </Link>
          </div>

          {/* Center: Theme toggle */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="rounded-full h-8 w-8 p-4"
            >
              <Sun className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              <Moon className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <span className="sr-only">Toggle theme</span>
            </Button>
          </div>

          {/* Right: User related & mobile menu */}
          <div className="ml-auto flex items-center space-x-4">       
            <div className="hidden sm:flex sm:items-center sm:space-x-4">
              {session ? (
                <UserProfileMenu onLogout={handleLogout} isAdmin={isAdmin} />
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-muted-foreground hover:text-primary transition-colors text-sm font-semibold pr-3"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
                    <SignupButton />
                  </Link>
                </>
              )}
            </div>

            <div className="sm:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center p-2 px-2.5 rounded-full"
              >
                <span className="sr-only">Open main menu</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isMenuOpen && (
        <div className="sm:hidden">
          <div className="px-2 pt-2 pb-3 space-y-2">
            <div className="px-3 py-2 space-y-3">
              {session ? (
                <>
                  <Link
                    to="/profile"
                    className="flex items-center px-3 py-2 text-base font-medium rounded-md hover:bg-accent"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="mr-3 h-5 w-5" />
                    Profile
                  </Link>
                  {isAdmin && (
                    <>
                      <Link
                        to="/admin/analytics"
                        className="flex items-center px-3 py-2 text-base font-medium rounded-md hover:bg-accent"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <BarChart3 className="mr-3 h-5 w-5" />
                        Analytics
                      </Link>
                      <Link
                        to="/admin/feedback"
                        className="flex items-center px-3 py-2 text-base font-medium rounded-md hover:bg-accent"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <MessageSquare className="mr-3 h-5 w-5" />
                        Feedback
                      </Link>
                    </>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="flex w-full items-center px-3 py-2 text-base font-medium rounded-md hover:bg-accent"
                  >
                    <LogOut className="mr-3 h-5 w-5" />
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Button variant="ghost" className="w-full justify-start px-3">
                      Log in
                    </Button>
                  </Link>
                  <Link
                    to="/signup"
                    className="block"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <SignupButton className="w-full justify-start px-3" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default NavigationBar;
